/**
 * RouterContextService — Centralized handling of Router context errors
 * 
 * Provides unified error handling, Sentry logging, and deduplication
 * for cases when React Router hooks are called outside Router context.
 */

import * as Sentry from '@sentry/react';
import { logger } from './logger';

/** Time window for deduplication (5 seconds) */
const DEDUP_WINDOW_MS = 5_000;

/** Map for tracking recent errors to prevent spam */
const errorDedup = new Map<string, number>();

/** Total error counter for metrics */
let totalContextErrors = 0;

/**
 * Types of Router context errors we handle
 */
export type RouterContextErrorType = 
  | 'useNavigate_missing_context'
  | 'useLocation_missing_context'
  | 'useParams_missing_context'
  | 'useSearchParams_missing_context';

/**
 * Structured info about a Router context error
 */
export interface RouterContextErrorInfo {
  hookName: string;
  errorType: RouterContextErrorType;
  route: string;
  timestamp: string;
  recoveryMethod: 'window_fallback' | 'reload' | 'none';
  userAgent?: string;
}

/**
 * Check if an error is a Router context error
 */
export function isRouterContextError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message;
  return (
    message.includes('useNavigate() may be used only in the context of a <Router>') ||
    message.includes('useLocation() may be used only in the context of a <Router>') ||
    message.includes('useParams() may be used only in the context of a <Router>') ||
    message.includes('useSearchParams() may be used only in the context of a <Router>')
  );
}

/**
 * Check if we should report this error (deduplication)
 */
function shouldReport(key: string): boolean {
  const now = Date.now();
  const last = errorDedup.get(key);
  if (last && now - last < DEDUP_WINDOW_MS) return false;
  errorDedup.set(key, now);
  return true;
}

/**
 * Handle a Router context error with centralized logging
 * 
 * @param hookName - Name of the hook that failed (e.g., 'useNavigate', 'useLocation')
 * @param error - The caught error
 * @param recoveryMethod - How we're recovering from the error
 */
export function handleRouterContextError(
  hookName: string,
  error: unknown,
  recoveryMethod: 'window_fallback' | 'reload' | 'none' = 'window_fallback'
): void {
  totalContextErrors++;
  
  const route = typeof window !== 'undefined' ? window.location.pathname : '/unknown';
  const dedupKey = `${hookName}:${route}`;
  const shouldLog = shouldReport(dedupKey);
  
  const errorInfo: RouterContextErrorInfo = {
    hookName,
    errorType: `${hookName}_missing_context` as RouterContextErrorType,
    route,
    timestamp: new Date().toISOString(),
    recoveryMethod,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  };
  
  // Always add breadcrumb for context (helps debug subsequent errors)
  Sentry.addBreadcrumb({
    category: 'router',
    message: `Router context error in ${hookName}`,
    level: 'warning',
    data: {
      hook: hookName,
      route: errorInfo.route,
      recovery: recoveryMethod,
    },
  });
  
  // Only log and send to Sentry if not a duplicate
  if (shouldLog) {
    // Log as warning (expected during HMR/lazy loading race conditions)
    logger.warn(`Router context unavailable in ${hookName}, using ${recoveryMethod}`, {
      context: {
        feature: 'router',
        component: hookName,
        route: errorInfo.route,
      },
      data: {
        hookName: errorInfo.hookName,
        errorType: errorInfo.errorType,
        route: errorInfo.route,
        recoveryMethod: errorInfo.recoveryMethod,
        timestamp: errorInfo.timestamp,
      },
      allowList: ['hookName', 'errorType', 'route', 'recoveryMethod', 'timestamp'],
      error_code: 'router_context_missing',
      error_type: 'ui',
    });
    
    // Send to Sentry as warning (not error - this is expected behavior)
    Sentry.captureMessage(`Router context missing: ${hookName}`, {
      level: 'warning',
      tags: {
        hook: hookName,
        route: errorInfo.route,
        recovery: recoveryMethod,
      },
      extra: {
        errorInfo,
        totalContextErrors,
        uniqueErrors: errorDedup.size,
      },
    });
  }
}

/**
 * Get current error statistics
 */
export function getRouterContextErrorStats(): { total: number; unique: number } {
  return {
    total: totalContextErrors,
    unique: errorDedup.size,
  };
}

/**
 * Clear error statistics (useful for testing)
 */
export function clearRouterContextErrorStats(): void {
  totalContextErrors = 0;
  errorDedup.clear();
}

/**
 * Exported service object for convenient usage
 */
export const routerContextService = {
  isRouterContextError,
  handleError: handleRouterContextError,
  getStats: getRouterContextErrorStats,
  clearStats: clearRouterContextErrorStats,
};
